import { useMemo } from 'react';

import {
  isDisabledConfig,
  useEditorState,
} from './context/VideoEditorStateContext';
import useVideo from './useVideo';

export interface VideoClipInstanceLock {
  disabled: true;
  tooltip?: string;
}

/**
 * Resolves the all-or-nothing instance lock for the video clip identified by
 * `clipId`. Returns `undefined` when the clip has no snapshotted
 * `templateIndex` (e.g. clips added at runtime) or when no relevant lockSpec
 * entry applies.
 *
 * Granular per-property locks (remove vs style vs position vs size)
 * intentionally collapse into a single flag at the editor layer — see
 * SPAR-27811 for the rationale. Targeting is by snapshotted index only;
 * id-targeted lockSpec entries remain queryable via
 * `lock.can('videoClips', cap, { id })` for ad-hoc checks but are not
 * surfaced through this hook.
 */
export default function useVideoClipLockedFeatures(
  clipId: string,
): VideoClipInstanceLock | undefined {
  const { features } = useEditorState();
  const clip = useVideo(clipId);
  const templateIndex = clip?.templateIndex;

  return useMemo(() => {
    // Runtime-added clips (and bogus ids) carry no templateIndex; they're
    // never pre-locked, even when the asset-type default is `'locked'`.
    if (templateIndex === undefined) {
      return undefined;
    }

    const videoClips = features?.videoClips;

    if (videoClips === 'locked') {
      return { disabled: true };
    }

    if (typeof videoClips !== 'object') {
      return undefined;
    }

    const slotState = videoClips.byIndex?.[templateIndex];
    if (!isDisabledConfig(slotState)) {
      return undefined;
    }

    return { disabled: true, tooltip: slotState.reason };
  }, [features, templateIndex]);
}
